import { runInAction, toJS } from "mobx";
import { useLocalObservable } from "mobx-react";

import {
  UserManagementStore,
  UserManagementFilter,
  FilterInput,
} from "./types";
import { ROLE_FILTER_KEY, USER_ROLES } from "./constants";
import { FilterType } from "../../apiclients/api/types/FilterType";

export const useUserManagementStore = () => {
  return useLocalObservable<UserManagementStore>(() => ({
    filters: [],
    filtersOrder: [],
    searchFilters: [],
    uncheckedColumns: new Set(),

    /**
     * Add given key to a unchecked select all list
     * @param value string
     */
    addUncheckedColumn(value: string) {
      runInAction(() => {
        this.uncheckedColumns.add(value);
      });
    },

    /**
     * Remove given key to a unchecked select all list
     * @param value string
     */
    removeUncheckedColumn(value: string) {
      runInAction(() => {
        this.uncheckedColumns.delete(value);
      });
    },

    /**
     * Reset unchecked select all list
     */
    resetUncheckedColumns() {
      runInAction(() => {
        this.uncheckedColumns = new Set();
      });
    },

    hideAllRows() {
      return Boolean(this.uncheckedColumns.size);
    },

    /**
     * Apply filter
     */
    applyFilter(newFilter: UserManagementFilter) {
      runInAction(() => {
        if (!this.filters.length) {
          this.filters = [newFilter];
        } else {
          // Remove new filter's previous value
          this.filters = this.filters.filter(
            (item) => item.key !== newFilter.key
          );
          // Apply new filter
          this.filters.push(newFilter);
        }
        if (!this.filtersOrder.includes(newFilter.key)) {
          this.filtersOrder.push(newFilter.key);
        }
      });
    },

    /**
     * Remove filter
     */
    removeFilter(filterKey: UserManagementFilter["key"]) {
      runInAction(() => {
        this.filters = this.filters.filter((item) => filterKey !== item.key);
        this.filtersOrder = this.filtersOrder.filter((v) => filterKey !== v);
      });
    },

    resetAll() {
      this.resetFilters();
      this.resetSearchFilters();
      this.resetUncheckedColumns();
      runInAction(() => {
        this.filtersOrder = [];
      });
    },

    /**
     * Reset filters
     */
    resetFilters() {
      runInAction(() => {
        this.filters = [];
      });
    },

    /**
     * Reset filters
     */
    resetSearchFilters() {
      runInAction(() => {
        this.searchFilters = [];
      });
    },

    /**
     * Return true if some filter is active
     */
    hasActiveFilter(columnKey?: string) {
      if (columnKey) {
        return (
          this.filters.findIndex((filter) => filter.key === columnKey) >= 0 ||
          this.uncheckedColumns.has(columnKey)
        );
      }
      return Boolean(this.filters.length);
    },

    getFilterByKey(value: string) {
      return this.filters.filter((filter) => filter.key === value).pop();
    },

    getRoleFilters(previousFilters?: string[]) {
      const roleKeys = Object.keys(USER_ROLES);
      const roleFilters: UserManagementFilter[] = [];
      const includeValues: string[] = [];
      const excludeValues: string[] = [];
      this.filters
        .filter((filter) => {
          if (previousFilters) {
            return previousFilters.includes(filter.key);
          }
          return true;
        })
        .forEach((filter) => {
          const isRoleFilter =
            roleKeys.includes(filter.key) &&
            filter.filterType === FilterType.Bool;
          if (!isRoleFilter) {
            return;
          }
          const roleId = USER_ROLES[filter.key].toString();
          if (roleId) {
            if (filter.value) {
              includeValues.push(roleId);
            } else {
              excludeValues.push(roleId);
            }
          }
        });
      if (includeValues.length) {
        roleFilters.push({
          key: ROLE_FILTER_KEY,
          value: includeValues,
          exclude: false,
          filterType: FilterType.AutoComplete,
        });
      }
      if (excludeValues.length) {
        roleFilters.push({
          key: ROLE_FILTER_KEY,
          value: excludeValues,
          exclude: true,
          filterType: FilterType.AutoComplete,
        });
      }
      return roleFilters;
    },

    getColumnFilters(columnKey: string) {
      const roleKeys = Object.keys(USER_ROLES);
      const previousFilters = toJS(this.getFiltersBefore(columnKey));
      const roleFilters = this.getRoleFilters(previousFilters);
      // Exclude Current cell from filtering self data
      const dataFilters = toJS(
        this.filters.filter(
          (filter) =>
            filter.key !== columnKey &&
            previousFilters.includes(filter.key) &&
            !roleKeys.includes(filter.key)
        )
      );
      // Grab Current search filter
      const inputFilter = toJS(
        this.searchFilters.filter(
          (searchFilter) => searchFilter.key === columnKey
        )
      );

      return [...dataFilters, ...inputFilter, ...roleFilters];
    },

    getGridFilters() {
      const roleKeys = Object.keys(USER_ROLES);
      const roleFilters = this.getRoleFilters();
      const filters = toJS(this.filters).filter(
        (filter) => !roleKeys.includes(filter.key)
      );
      return [...toJS(this.searchFilters), ...filters, ...roleFilters];
    },

    getFiltersBefore(columnKey: string) {
      const index = this.filtersOrder.indexOf(columnKey);
      if (index === -1) {
        return this.filtersOrder;
      }
      return this.filtersOrder.slice(0, index);
    },

    /**
     * Remove Search filter
     */
    removeSearchFilter(filterKey: UserManagementFilter["key"]) {
      runInAction(() => {
        this.searchFilters = this.searchFilters.filter(
          (item) => filterKey !== item.key
        );
      });
    },

    /**
     * Apply Search filter
     */
    applySearchFilter(newFilter: FilterInput) {
      runInAction(() => {
        if (!this.searchFilters.length) {
          this.searchFilters = [newFilter];
        } else {
          // Remove new filter's previous value
          this.searchFilters = this.searchFilters.filter(
            (item) => item.key !== newFilter.key
          );
          // Apply new filter
          this.searchFilters.push(newFilter);
        }
      });
    },
  }));
};
